import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigurationService } from '@app/configuration/configuration.service';
import { DataLoader } from '@app/dao/data-loader';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { UtilsService } from '@app/providers/utils/utils.service';
import { LoadingPageService } from '@app/services/loading-page.service';
import { OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { jwtDecode } from 'jwt-decode';
import { combineLatest, from, Observable, of, Subject, throwError, catchError, filter, map, switchMap, tap } from 'rxjs';
import { UserAuthentified } from './user-authentified.interface';

export const DEFAULT_PAGE = '/dashboard/global';
// Clé dans le local storage pour stocker le token d'autologin
const AUTOLOGIN_TOKEN = 'autologin_token';

@Injectable({ providedIn: 'root' })
export class AuthentificationService {
  private authenticationSubject = new DataLoader<UserAuthentified>(() =>
    this.httpApi.get('/user/authentificated').pipe(
      map(res => res.data),
      tap(user => this.subscriptionFacade.setUser(user)),

      catchError(err => {
        if (err.status == 401) {
          this.storage?.removeItem(AUTOLOGIN_TOKEN);
          this.oauthService?.logOut(true);
          window.location.reload();
        }

        return throwError(err);
      }),
    ),
  );
  public get authentication(): Observable<UserAuthentified> {
    return this.authenticationSubject.get();
  }

  public resetAuthentication() {
    this.authenticationSubject.reset();
  }

  public logoutSubject = new Subject();

  /**
   * Publishes `true` if and only if (a) all the asynchronous initial
   * login calls have completed or errored, and (b) the user ended up
   * being authenticated.
   *
   * In essence, it combines:
   *
   * - the latest known state of whether the user is authorized
   * - whether the ajax calls for initial log in have all been done
   */
  public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.configurationService.isDoneLoading,
    this.configurationService.isAuthenticated,
  ]).pipe(
    map(([isDoneLoading, isAuthenticated]) => isDoneLoading && isAuthenticated),
  );

  constructor(
    private httpApi: HttpApiService,
    private oauthService: OAuthService,
    private router: Router,
    public route: ActivatedRoute,
    public utilsService: UtilsService,
    public loadingService: LoadingPageService,
    private configurationService: ConfigurationService,
    private subscriptionFacade: SubscriptionFacadeService,
    private storage: OAuthStorage,
  ) {
    let previous = false;

    this.configurationService.isAuthenticated.subscribe(isAuthenticated => {
      if (previous !== isAuthenticated) {
        previous = isAuthenticated;
        if (isAuthenticated) {
          this.authenticationSubject.reset();
          this.authenticationSubject.get();
        } else {
          this.authenticationSubject.reset();
        }
      }
    });
  }

  public hasRole(role: string) {
    let claims: any = this.oauthService.getIdentityClaims();
    if (claims && claims.groups) {
      let roles: string[] = claims.groups;
      roles = roles.map(role => role.toUpperCase());
      return roles.includes(role.toLocaleUpperCase());
    }
    return false;
  }

  public getFullname() {
    let claims: any = this.oauthService.getIdentityClaims();
    if (claims && claims.name) {
      return claims.name;
    }
    return undefined;
  }

  public login(targetUrl?: string) {
    let url = targetUrl || this.router.url;
    this.oauthService.initLoginFlow(url);
  }

  public logout() {
    this.storage?.removeItem(AUTOLOGIN_TOKEN);
    this.oauthService?.revokeTokenAndLogout().then(() => {
      window.location.hash = '#' + DEFAULT_PAGE;
      window.location.reload();
    });
  }

  public hasValidToken() {
    return this.oauthService.hasValidAccessToken();
  }

  public getAccessToken() {
    return this.oauthService.getAccessToken();
  }

  // FIXME: unused method
  public loadUser() {
    const loadData$ = this.loadingService.showLoadingUntilCompleted(
      this.authentication,
    );
    return loadData$.subscribe(res => {});
  }

  autologin(token: string, userAccountId: string): void {
    // Client public : pas de secret, angular-oauth2-oidc ajoute le client_id au corps de la requête.
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    this.oauthService?.logOut(true);

    from(
      this.oauthService
        .fetchTokenUsingGrant(
          'autologin',
          { autologin_token: token, autologin_user_account_id: userAccountId },
          headers,
        )
        .then(async res => {
          const { access_token: accessToken } = res;
          const { aid: accountId } = jwtDecode<any>(accessToken);

          this.storage.setItem(AUTOLOGIN_TOKEN, token);
          await this.getAccountAutologin(
            accountId,
            userAccountId,
            token,
          ).subscribe(result => {
            this.router.navigateByUrl(result.redirectUri);
          });
        }),
    );
  }

  getAccountAutologin(
    accountId: string,
    userAccountId: string,
    token: string,
  ): Observable<any> {
    return this.httpApi.get(
      `/accounts/${accountId}/autologin?token=${token}&client=ESPACE_CLIENT&userAccountId=${userAccountId}`,
    );
  }

  public get autologinToken(): string {
    return this.storage.getItem(AUTOLOGIN_TOKEN);
  }

  public getUserType(): Observable<'consultant' | 'client'> {
    return this.configurationService.isAuthenticated.pipe(
      switchMap(authenticated =>
        authenticated
          ? this.authentication.pipe(filter(user => !!user))
          : of(null),
      ),
      map(user => {
        if (!user?.sessionConsultant) {
          return 'client';
        } else {
          return 'consultant';
        }
      }),
    );
  }
  public isConsultant(): Observable<boolean> {
    return this.getUserType().pipe(map(type => type === 'consultant'));
  }

  public isClient(): Observable<boolean> {
    return this.getUserType().pipe(map(type => type === 'client'));
  }
}
